import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTE } from '../../routes';

/**
 * Hub to area and back. `PageTransition` reads slide direction from the history index, so a push to the hub would
 * slide forward although the owner is going back. The hub therefore marks its links with {@link HUB_LINK_STATE},
 * and the area's back button pops (a real back slide) when its entry carries that mark and has an in-app entry
 * behind it; otherwise it pushes the hub, which still lands there.
 */

/** The router state every hub link and hub action navigates with. */
export const HUB_LINK_STATE = { fromHub: true } as const;

/** Whether a location state is the hub's mark (an object whose `fromHub` is exactly `true`). */
export const isFromHub = (state: unknown): boolean =>
  typeof state === 'object' && state !== null && 'fromHub' in state && state.fromHub === true;

/** Pop back to the hub when the entry came from it and is not the first in-app entry; else push the hub. */
export const backToWebsiteMode = (state: unknown, historyIdx: number): 'pop' | 'push' =>
  isFromHub(state) && historyIdx > 0 ? 'pop' : 'push';

/**
 * An area header's back action: pops to its hub (`hubPath`) when it can, pushes the hub otherwise. A button (the
 * drilldown header's), so there is no link to open in a new tab and no modified click to leave to the browser.
 * Shared by the Website and Marketing hubs, whose links both carry {@link HUB_LINK_STATE}.
 */
export const useBackToHub = (hubPath: string): (() => void) => {
  const location = useLocation();
  const navigate = useNavigate();
  return (): void => {
    const idx = (window.history.state as { idx?: unknown } | null)?.idx;
    if (backToWebsiteMode(location.state, typeof idx === 'number' ? idx : 0) === 'pop') navigate(-1);
    else navigate(hubPath);
  };
};

/** The Website area header's back action (see {@link useBackToHub}). */
export const useBackToWebsite = (): (() => void) => useBackToHub(ROUTE.website());
